import React, { useRef, useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface TabsSliderProps {
  children: React.ReactNode;
  className?: string;
  containerClassName?: string;
  showArrows?: boolean;
}

export const TabsSlider: React.FC<TabsSliderProps> = ({
  children,
  className = "",
  containerClassName = "",
  showArrows = true,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeftState, setScrollLeftState] = useState(0);

  const checkScrollability = () => {
    if (!scrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    setCanScrollLeft(scrollLeft > 4);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 4);
  };

  useEffect(() => {
    checkScrollability();
    window.addEventListener("resize", checkScrollability);
    return () => window.removeEventListener("resize", checkScrollability);
  }, [children]);

  const handleScroll = (direction: "left" | "right") => {
    if (!scrollRef.current) return;
    const scrollAmount = Math.max(scrollRef.current.clientWidth * 0.65, 200);
    scrollRef.current.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
    setTimeout(checkScrollability, 300);
  };

  // Mouse Drag to Scroll handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!scrollRef.current) return;
    setIsDragging(true);
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setScrollLeftState(scrollRef.current.scrollLeft);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 1.5;
    scrollRef.current.scrollLeft = scrollLeftState - walk;
    checkScrollability();
  };

  const handleMouseUpOrLeave = () => {
    setIsDragging(false);
  };

  return (
    <div className={`relative flex items-center gap-1 group ${containerClassName}`}>
      {showArrows && (
        <button
          type="button"
          onClick={() => handleScroll("left")}
          disabled={!canScrollLeft}
          className={`p-1.5 rounded-lg border border-border bg-card/90 backdrop-blur text-muted-foreground hover:text-foreground hover:border-primary/50 transition-all flex-shrink-0 cursor-pointer shadow-sm z-10 ${
            !canScrollLeft ? "opacity-30 cursor-not-allowed" : "hover:scale-105 active:scale-95 text-primary"
          }`}
          aria-label="Desplazar izquierda"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
      )}

      {/* Fade left indicator */}
      {canScrollLeft && (
        <div className="absolute left-7 top-0 bottom-0 w-6 bg-gradient-to-r from-background to-transparent pointer-events-none z-[5]" />
      )}

      <div
        ref={scrollRef}
        onScroll={checkScrollability}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUpOrLeave}
        onMouseLeave={handleMouseUpOrLeave}
        className={`flex items-center gap-2 overflow-x-auto py-1 px-0.5 flex-1 min-w-0 no-scrollbar touch-pan-x select-none ${
          isDragging ? "cursor-grabbing" : "cursor-grab"
        } ${className}`}
        style={{
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          WebkitOverflowScrolling: "touch",
        }}
      >
        {children}
      </div>

      {/* Fade right indicator */}
      {canScrollRight && (
        <div className="absolute right-7 top-0 bottom-0 w-6 bg-gradient-to-l from-background to-transparent pointer-events-none z-[5]" />
      )}

      {showArrows && (
        <button
          type="button"
          onClick={() => handleScroll("right")}
          disabled={!canScrollRight}
          className={`p-1.5 rounded-lg border border-border bg-card/90 backdrop-blur text-muted-foreground hover:text-foreground hover:border-primary/50 transition-all flex-shrink-0 cursor-pointer shadow-sm z-10 ${
            !canScrollRight ? "opacity-30 cursor-not-allowed" : "hover:scale-105 active:scale-95 text-primary"
          }`}
          aria-label="Desplazar derecha"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};
